import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { atom, useAtom, useAtomValue } from "jotai";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import DepartmentIcon from "@whoosh/design-system/src/components/icons_v2/department/components/DepartmentIcon";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator";
import { navigationRef_atom } from "../../Navigation";
import {
  sessionCurrentClubSlugAtom,
  useIsCurrentUserPublic,
} from "../../Session";
import { LessonFacilitySwitcherModalFragment_session$key } from "./__generated__/LessonFacilitySwitcherModalFragment_session.graphql";

export const showLessonFacilitySwitcherModalAtom = atom<boolean>(false);

type Props = {
  sessionRef: LessonFacilitySwitcherModalFragment_session$key | null;
};

export const LessonFacilitySwitcherModal = ({ sessionRef }: Props) => {
  const [
    shouldShowLessonFacilitySwitcherModal,
    setShouldShowLessonFacilitySwitcherModal,
  ] = useAtom(showLessonFacilitySwitcherModalAtom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom);
  const navigationRef = useAtomValue(navigationRef_atom);

  const session = useFragment(
    LessonFacilitySwitcherModalFragmentString,
    sessionRef
  );

  const lessonNoun = getNounOverride(
    session?.currentClientProfile?.member?.club.copyOverrides?.lesson?.singular,
    t`lesson`,
    true
  );
  const isCurrentUserPublic = useIsCurrentUserPublic(session ?? null);

  const lessonFacilities =
    session?.currentClientProfile?.member?.club?.facilities?.edges?.flatMap(
      (e) => (e?.node ? [e.node] : [])
    ) ?? [];

  const facilitiesThatSupportLessonsForUserClass = lessonFacilities.filter(
    (facility) => {
      return (
        facility.isLessonsEnabled &&
        (isCurrentUserPublic
          ? facility.publicLessonsPermissionSet?.patronAccess !== "NONE"
          : facility.privateLessonsPermissionSet?.patronAccess !== "NONE")
      );
    }
  );

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const navigateToLessonsPage = (facilitySlug: string) => {
    if (clubSlug && navigationRef?.isReady()) {
      navigationRef.navigate("Club", {
        clubSlug,
        screen: "Lessons",
        params: {
          facilitySlug,
          screen: "LessonsHome",
        },
      });
    } else {
      navigation.navigate("Lessons", {
        facilitySlug,
        screen: "LessonsHome",
      });
    }

    setShouldShowLessonFacilitySwitcherModal(false);
  };

  return (
    <StandardModal
      upperTitle={`${t`Where do you want to book your`} ${lessonNoun}? `}
      isOpen={shouldShowLessonFacilitySwitcherModal}
      onClose={() => setShouldShowLessonFacilitySwitcherModal(false)}
      content={
        <VStack px={6}>
          <HStack flexWrap="wrap" gap={3}>
            {facilitiesThatSupportLessonsForUserClass.map((facility) => {
              const onUrlPress = () => navigateToLessonsPage(facility.slug);
              const departmentType = facility?.department?.type || "GOLF";
              return (
                <Pressable
                  onPress={onUrlPress}
                  key={facility.slug}
                  testID={`${facility.slug}-lesson-select-btn`}
                >
                  <VStack
                    bg="surface-main-primary-subtlest"
                    width={108}
                    height={108}
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="4"
                    flexDirection="column"
                    gap={3}
                  >
                    <DepartmentIcon type={departmentType} />
                    <Text
                      maxFontSizeMultiplier={1.25}
                      variant="mainSmallSemiBold"
                      textAlign="center"
                    >
                      {facility.name}
                    </Text>
                  </VStack>
                </Pressable>
              );
            })}
          </HStack>
        </VStack>
      }
    />
  );
};

const LessonFacilitySwitcherModalFragmentString = graphql`
  fragment LessonFacilitySwitcherModalFragment_session on Session {
    ...useIsCurrentUserPublic_session
    currentClientProfile {
      member {
        club {
          copyOverrides {
            lesson {
              singular
            }
          }
          facilities {
            edges {
              node {
                name
                slug
                isLessonsEnabled
                privateLessonsPermissionSet {
                  patronAccess
                }
                publicLessonsPermissionSet {
                  patronAccess
                }
                department {
                  type
                }
              }
            }
          }
        }
      }
    }
  }
`;
